import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import { completeLogin } from './session'

/**
 * Where the pool sends an author back to.
 *
 * The path is registered with the user pool, so it is fixed rather than
 * chosen here, and it is deliberately outside the session gate — this is
 * the one screen that runs *without* a credential in order to obtain one.
 */
export function AuthCallback() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  // StrictMode mounts effects twice in development, and an authorization
  // code is single-use — the second exchange would fail and report a
  // broken login that had in fact succeeded.
  const exchanged = useRef(false)

  useEffect(() => {
    if (exchanged.current) return
    exchanged.current = true

    const failure = params.get('error')
    if (failure) {
      setError(params.get('error_description') ?? failure)
      return
    }

    const code = params.get('code')
    const state = params.get('state')
    if (!code || !state) {
      setError('Sign-in did not finish. Please start again.')
      return
    }

    completeLogin(code, state)
      .then((returnTo) => navigate(returnTo, { replace: true }))
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : String(reason)))
  }, [params, navigate])

  if (error) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 3 }}>
        <Stack spacing={2} sx={{ maxWidth: 520 }}>
          <Alert severity="error">{error}</Alert>
          <Button onClick={() => navigate('/', { replace: true })}>Start again</Button>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  )
}
